import { useState } from "react";
import { Cpu, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { MacImportReport } from "./MacImportReport";
import { MAC_APPS, searchMac } from "@/lib/macLookup";

const label = (k) => (MAC_APPS.find(([a]) => a === k) || [k, k])[1];
const fmt = (v) => (v || "").replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12).match(/.{1,2}/g)?.join(":") || "";

export const MacMatch = ({ app, m, onOpen }) => (
  <div data-testid={`mac-match-${app}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-[#1F293D] bg-[#0B1019] px-3 py-2 text-xs">
    <span className="rounded bg-pink-500/15 px-1.5 py-0.5 font-semibold text-pink-300">{label(app)}</span>
    <span className="font-mono text-slate-200">{m.panel_user || "—"}</span>
    <span className="text-slate-500">validade {m.validade || "—"}</span>
    {m.clients.length === 1 ? (
      <button data-testid={`mac-open-client-${m.clients[0].id}`} onClick={() => onOpen?.(m.clients[0].id)}
        className="ml-auto rounded-md bg-emerald-500/15 px-2 py-1 font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/25">
        {m.clients[0].name || m.clients[0].panel_user} · {m.clients[0].account}{m.saved ? " · MAC gravado" : ""} → abrir ficha
      </button>
    ) : (
      <span className="ml-auto text-amber-300">{m.clients.length ? `${m.clients.length} fichas parecidas, confira` : "Não está no CRM (lead)"}</span>
    )}
  </div>
);

export const MacLookupBox = ({ onOpen }) => {
  const [mac, setMac] = useState("");
  const [app, setApp] = useState("all");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (mac.length !== 17) return toast.error("Digite o MAC completo (12 caracteres)");
    setBusy(true); setRes(null);
    try {
      const r = await searchMac(mac, app === "all" ? null : app);
      setRes(r);
      const hits = r.matches.filter((m) => m.clients.length === 1);
      if (hits.length === 1) onOpen?.(hits[0].clients[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao buscar nos painéis");
    }
    setBusy(false);
  };

  return (
    <div data-testid="mac-lookup-box" className="mb-4 rounded-xl border border-pink-500/25 bg-[#111827] p-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-pink-300"><Cpu className="h-4 w-4" /> Buscar por MAC</div>
        <select data-testid="mac-lookup-app" value={app} onChange={(e) => setApp(e.target.value)}
          className="rounded-lg border border-[#1F293D] bg-[#0B1019] px-2 py-2 text-sm text-slate-200 focus:border-pink-500 focus:outline-none">
          <option value="all">Todos os apps</option>
          {MAC_APPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input data-testid="mac-lookup-input" value={mac} onChange={(e) => setMac(fmt(e.target.value))}
          onKeyDown={(e) => e.key === "Enter" && run()} placeholder="51:B7:06:29:9E:D7"
          className="flex-1 rounded-lg border border-[#1F293D] bg-[#0B1019] px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-600 focus:border-pink-500 focus:outline-none" />
        <button data-testid="mac-lookup-btn" disabled={busy} onClick={run}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-pink-500 px-4 py-2 text-sm font-bold text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar nos painéis
        </button>
      </div>
      {res && (
        <div data-testid="mac-lookup-results" className="mt-2 space-y-1.5">
          {res.matches.map((m, i) => <MacMatch key={`${m.app}-${i}`} app={m.app} m={m} onOpen={onOpen} />)}
          {!res.matches.length && <div className="text-xs text-slate-500">MAC não encontrado em nenhum painel pesquisado.</div>}
          {Object.entries(res.errors || {}).map(([a, e]) => <div key={a} className="text-[11px] text-red-300">{label(a)}: {e}</div>)}
        </div>
      )}
      <MacImportReport onOpen={onOpen} />
    </div>
  );
};
